import {
  type CustomServiceInventoryItem,
  type CustomServiceToolItem,
  conversationInventoryKgInsightKey,
  conversationInventoryKnowledgeGraphKey,
  type GoogleServiceItem,
  type InventoryEconomicCalendarItem,
  type InventoryInsightItemWithTitle,
  type InventoryKgInsightItem,
  isScenarioAnalysisItem,
  isScreenAnalysisItem,
  type MicrosoftServiceItem,
  type NotionServiceItem,
} from '@api/api/inventory-schema';
import { isEarningsCategory } from '@terminal/widgets/catalyst-insights-drawer/catalyst-widget/utils/catalyst-widget-utils';

import { normalizeFinancialStatementItems } from '../../financial-statements-item/financialStatementUtils';
import { type ConversationInventory } from '../../services/inventory-service';

const orEmpty = <T>(value: T[] | null | undefined): T[] => value ?? [];

export const groupCustomServiceItems = (
  items: CustomServiceToolItem[]
): CustomServiceInventoryItem[] => {
  const grouped = new Map<string, CustomServiceToolItem[]>();
  items.forEach(item => {
    const existing = grouped.get(item.connector);
    if (existing) {
      existing.push(item);
    } else {
      grouped.set(item.connector, [item]);
    }
  });

  return Array.from(grouped, ([name, groupedItems]) => ({
    name,
    items: groupedItems,
  }));
};

export const isProvenanceBundleArtifactId = (artifactId: string): boolean =>
  artifactId.startsWith('bundle_') || artifactId.startsWith('bundle-');

export type ArtifactInventoryItem = {
  artifact_id: string;
  displayTitle?: string | null;
  artifactType?: string | null;
  artifact_type?: string | null;
  hidden?: boolean | null;
  is_hidden?: boolean | null;
};

export const isHiddenArtifactInventoryItem = (
  artifact: ArtifactInventoryItem
): boolean => {
  const artifactType = artifact.artifactType ?? artifact.artifact_type;
  return (
    artifact.hidden === true ||
    artifact.is_hidden === true ||
    artifactType === 'provenance-content' ||
    artifact.artifact_id.startsWith('prov_content_')
  );
};

export const getVisibleArtifactItems = <T extends ArtifactInventoryItem>(
  artifacts: T[]
): T[] =>
  artifacts.filter(artifact => !isHiddenArtifactInventoryItem(artifact));

export const splitKgInsightItems = (items: InventoryKgInsightItem[]) => ({
  kgEarningsInsights: items.filter(item => isEarningsCategory(item.category)),
  kgCatalystInsights: items.filter(item => !isEarningsCategory(item.category)),
});

export const getInventoryData = (data: ConversationInventory | undefined) => {
  const artifacts = getVisibleArtifactItems(orEmpty(data?.artifact?.items));
  const news = orEmpty(data?.news?.items);
  const webLinks = orEmpty(
    data?.['web-links']?.items?.flatMap(item => item.urls)
  );
  const insights = orEmpty(data?.insight?.items);
  const externalDataLinks = orEmpty(
    data?.['external-data']?.items?.flatMap(item => orEmpty(item.links))
  );
  const financialStatements = normalizeFinancialStatementItems(
    orEmpty(data?.['financial-statement']?.items)
  );
  const entities = orEmpty(data?.entity?.items?.map(item => item.tag));
  const timeSeriesItems = orEmpty(data?.timeseries?.items);
  const documentSearchItems = orEmpty(data?.['document-search']?.items);
  const watchlistItems = orEmpty(data?.watchlist?.items);
  const kgItems = orEmpty(
    data?.[conversationInventoryKnowledgeGraphKey]?.items
  );
  const economicCalendarItems: InventoryEconomicCalendarItem[] = orEmpty(
    data?.['economic-calendar']?.items
  );
  const oneDriveItems: MicrosoftServiceItem[] = orEmpty(
    data?.['microsoft_onedrive']?.items
  );
  const outlookItems: MicrosoftServiceItem[] = orEmpty(
    data?.['microsoft_outlook']?.items
  );
  const oneNoteItems: MicrosoftServiceItem[] = orEmpty(
    data?.['microsoft_onenote']?.items
  );
  const teamsItems: MicrosoftServiceItem[] = orEmpty(
    data?.['microsoft_teams']?.items
  );
  const notionItems: NotionServiceItem[] = orEmpty(data?.notion_notion?.items);
  const googleCalendarItems: GoogleServiceItem[] = orEmpty(
    data?.google_calendar?.items
  );
  const googleDriveItems: GoogleServiceItem[] = orEmpty(
    data?.google_drive?.items
  );
  const googleDocsItems: GoogleServiceItem[] = orEmpty(
    data?.google_docs?.items
  );
  const earningsInsights: InventoryInsightItemWithTitle[] = orEmpty(
    data?.['earnings-insight']?.items
  );
  const catalystInsights: InventoryInsightItemWithTitle[] = orEmpty(
    data?.['catalyst-insight']?.items
  );
  const { kgEarningsInsights, kgCatalystInsights } = splitKgInsightItems(
    orEmpty(data?.[conversationInventoryKgInsightKey]?.items)
  );
  const analysisItems = orEmpty(data?.analysis?.items);
  const screenItems = analysisItems.filter(isScreenAnalysisItem);
  const scenarioItems = analysisItems.filter(isScenarioAnalysisItem);
  const customMcpConnectors = groupCustomServiceItems(
    orEmpty(data?.custom_service?.items)
  );

  return {
    artifacts,
    news,
    webLinks,
    insights,
    externalDataLinks,
    financialStatements,
    entities,
    timeSeriesItems,
    documentSearchItems,
    watchlistItems,
    kgItems,
    economicCalendarItems,
    oneDriveItems,
    outlookItems,
    oneNoteItems,
    teamsItems,
    notionItems,
    googleCalendarItems,
    googleDriveItems,
    googleDocsItems,
    earningsInsights,
    catalystInsights,
    kgEarningsInsights,
    kgCatalystInsights,
    screenItems,
    scenarioItems,
    customMcpConnectors,
  };
};
